import React, { useState } from 'react';
import { Mail, MapPin, Clock, Send } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';

const Contato = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      console.log('Form submitted:', formData);
      // In production, this would send to info@massimohomeimprovement.com
      toast.success('Message sent successfully! We will contact you shortly.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#F8E6C6] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-[#D7AC78] font-semibold text-sm uppercase tracking-wider mb-4 block">
              Get In Touch
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-[#484237] mb-6 leading-tight">
              Let's Talk About Your Project
            </h1>
            <p className="text-xl text-[#728481] leading-relaxed">
              Schedule a free consultation and discover how we can transform your spaces
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-[#484237] mb-6">
                Contact Information
              </h2>
              <p className="text-lg text-[#728481] mb-8 leading-relaxed">
                We are ready to serve you. Contact us through the channels below 
                or fill out the form to schedule a consultation.
              </p>

              <div className="space-y-6 mb-10">
                <div className="flex items-start space-x-4 p-4 bg-[#F8E6C6]/30 rounded-xl hover:bg-[#F8E6C6]/50 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#D7AC78] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#484237] mb-1">Email</h3>
                    <a 
                      href={`mailto:${companyInfo.contact.email}`}
                      className="text-[#728481] hover:text-[#D7AC78] transition-colors"
                    >
                      {companyInfo.contact.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-[#F8E6C6]/30 rounded-xl hover:bg-[#F8E6C6]/50 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#D7AC78] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#484237] mb-1">Location</h3>
                    <p className="text-[#728481]">{companyInfo.contact.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-[#F8E6C6]/30 rounded-xl hover:bg-[#F8E6C6]/50 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#D7AC78] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#484237] mb-1">Business Hours</h3>
                    <p className="text-[#728481]">{companyInfo.contact.hours}</p>
                  </div>
                </div>
              </div>

              {/* Image */}
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4"
                  alt="Professional Consultation"
                  className="w-full h-[300px] object-cover"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gradient-to-br from-[#F8E6C6]/50 to-white p-8 rounded-2xl shadow-xl">
              <h2 className="text-3xl font-bold text-[#484237] mb-6">
                Schedule Consultation
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#484237] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-[#CDD2C5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7AC78] focus:border-transparent transition-all duration-300"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#484237] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-[#CDD2C5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7AC78] focus:border-transparent transition-all duration-300"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#484237] mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-[#CDD2C5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7AC78] focus:border-transparent transition-all duration-300"
                    placeholder="(678) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-[#484237] mb-2">
                    Service of Interest
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#CDD2C5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7AC78] focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Select a service</option>
                    <option value="residential">Residential Renovation</option>
                    <option value="commercial">Commercial Renovation</option>
                    <option value="consultation">Design Consultation</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#484237] mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-white border border-[#CDD2C5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D7AC78] focus:border-transparent transition-all duration-300 resize-none"
                    placeholder="Tell us about your project..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center space-x-2 px-8 py-4 bg-[#D7AC78] text-white font-medium rounded-lg hover:bg-[#c49a68] transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      <span>Send Message</span>
                    </>
                  )}
                </button>

                <p className="text-sm text-[#728481] text-center">
                  We will respond to your message within 24 business hours
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contato;
